import React, { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { useGetAchievement } from '~/hooks/apis/agent/useGetAchievement';

import { formatDateSlashes } from '~/services/TimeService';

import { AchievementDetail } from '~/types/services/agentHub';

import { capitalise } from '~/utils/stringUtils';

import AchievementSubjectName from './AchievementSubjectName';
import { buildRewardConditionBreakdown } from './rewardForm';

type AchievementLog = AchievementDetail['logs'][number];

interface Props {
  achievementId?: string;
  open: boolean;
  onClose: () => void;
}

export default function AchievementDialog({ achievementId, open, onClose }: Props) {
  const { data, isFetching, error } = useGetAchievement(open ? achievementId : undefined);
  const [conditionsJson, setConditionsJson] = useState<AchievementLog['evaluatedConditions'] | null>(null);

  const handleViewJson = useCallback((event: React.MouseEvent<HTMLButtonElement>, log: AchievementLog) => {
    event.stopPropagation();
    setConditionsJson(log.evaluatedConditions);
  }, []);

  const handleCloseJson = useCallback(() => {
    setConditionsJson(null);
  }, []);

  const logColumns = useMemo<Array<GridColDef<AchievementLog>>>(
    () => [
      {
        field: 'createdAt',
        headerName: 'When',
        flex: 1,
        valueFormatter: (value) => formatDateSlashes(typeof value === 'string' ? value : undefined),
      },
      {
        field: 'event',
        headerName: 'Event',
        flex: 1,
        valueFormatter: (value) => capitalise(String(value)),
      },
      {
        field: 'evaluatedConditions',
        headerName: 'Conditions',
        flex: 2,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<AchievementLog>) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {row.evaluatedConditions
                .map(
                  (item) =>
                    `${item.condition.type} ${item.condition.operator} ${item.condition.threshold} (current ${item.currentValue}, ${item.satisfies ? 'met' : 'not met'})`,
                )
                .join('; ') || '-'}
            </Typography>
            <Button variant="text" color="primary" onClick={(event) => handleViewJson(event, row)}>
              View JSON
            </Button>
          </Stack>
        ),
      },
    ],
    [handleViewJson],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Achievement</DialogTitle>
      <DialogContent>
        {isFetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">Unable to load this achievement.</Alert>}
        {data && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">Status: {capitalise(data.status)}</Typography>
            <Typography variant="body2">
              Subject: {capitalise(data.subjectType)}{' '}
              <AchievementSubjectName linked subjectType={data.subjectType} subjectId={data.subjectId} />
            </Typography>
            <Typography variant="body2">Earned: {formatDateSlashes(data.earnedAt)}</Typography>
            <Typography variant="subtitle1">Reward</Typography>
            <Typography variant="body2">Name: {data.reward.name}</Typography>
            <Typography variant="body2">Description: {data.reward.description}</Typography>
            <Typography variant="body2">Type: {capitalise(data.reward.rewardType)}</Typography>
            <Typography variant="body2">Audience: {capitalise(data.reward.targetAudience)}</Typography>
            <Typography variant="body2">
              Window: {formatDateSlashes(data.reward.startDate)} – {formatDateSlashes(data.reward.endDate)}
            </Typography>
            <Typography variant="body2">Expiry: {formatDateSlashes(data.reward.expiryDate)}</Typography>
            <Typography variant="body2">
              Conditions:{' '}
              {buildRewardConditionBreakdown(data.reward.conditions).join(` ${data.reward.operator} `) || '-'}
            </Typography>
            <Typography variant="body2">
              Commission:{' '}
              {data.reward.commissions[0]?.commissionPercentage != null
                ? `${data.reward.commissions[0].commissionPercentage}%`
                : '-'}
            </Typography>
            <Typography variant="subtitle1">Logs</Typography>
            <DataGrid
              rows={data.logs}
              columns={logColumns}
              getRowId={(row) => row.id}
              autoHeight
              hideFooter
              disableRowSelectionOnClick
            />
          </Stack>
        )}
      </DialogContent>
      <Dialog open={conditionsJson !== null} onClose={handleCloseJson} fullWidth maxWidth="md">
        <DialogTitle>Evaluated conditions</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              bgcolor: 'background.paper',
              maxHeight: 480,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <Typography component="code" variant="body2" sx={{ fontFamily: 'monospace' }}>
              {JSON.stringify(conditionsJson, null, 2)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="primary" onClick={handleCloseJson}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
